import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { textOn } from '../logo-rail/textOn.ts'
import styles from './FixtureCard.module.css'

export interface FixtureCardSide {
  name: string
  /** The team's logo. Without one, its initials sit on `color`. */
  logoUrl?: string | null
  /** Brand colour behind the initials when there is no logo. */
  color?: string
  /** "3–2", "3rd · 3–2". Omitted until the team has played. */
  record?: string
  /** The opponent reads one step quieter than your own side. */
  muted?: boolean
}

export interface FixtureCardProps {
  /** Where the card goes: the match, or the competition. */
  to: string
  /** "Today", "Mon · Nov 2". */
  when: string
  /** "12:22 PM". Leave it out when the time is not known yet. */
  time?: string
  live?: boolean
  /** The next thing up. Takes a stronger border so the row has a start. */
  emphasis?: boolean
  /** A match: your side first. */
  sides?: [FixtureCardSide, FixtureCardSide]
  /**
   * Not a match — a competition starting, or one open to enter. Logo, a title
   * and one line of detail stand in for the two sides.
   */
  logo?: ReactNode
  title?: ReactNode
  detail?: ReactNode
  /** Right of the date line: "You run it", "Open". */
  tag?: ReactNode
  /** The bottom line: game mark and competition, or a call to action. */
  footer?: ReactNode
  /** Dashed and unfilled, for something you could join rather than something you're in. */
  dashed?: boolean
}

function initials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean)
  return (words.length > 1 ? words[0][0] + words[1][0] : name.slice(0, 2)).toUpperCase()
}

function Side({ side }: { side: FixtureCardSide }) {
  const fg = side.color ? textOn(side.color) : undefined
  return (
    <span className={[styles.side, side.muted ? styles.muted : ''].filter(Boolean).join(' ')}>
      {side.logoUrl
        ? <img src={side.logoUrl} alt="" className={styles.mark} />
        : (
          <span
            className={`${styles.mark} ${styles.initials}`}
            style={side.color && fg ? { background: side.color, color: fg } : undefined}
            aria-hidden="true"
          >
            {initials(side.name)}
          </span>
        )}
      <span className={styles.name}>{side.name}</span>
      {side.record && <span className={styles.record}>{side.record}</span>}
    </span>
  )
}

/**
 * One fixture as a card, for a row you scroll sideways.
 *
 * A schedule PAGE wants FixtureRow: one line per match under a date heading.
 * A home page wants a glance — both teams with their logos and records, the
 * time and the game — and a handful of these in a row does that where a list
 * of text rows does not. Things that are not matches (a competition starting,
 * one open to enter) take the same card so the row reads as one week.
 */
export function FixtureCard({
  to,
  when,
  time,
  live = false,
  emphasis = false,
  sides,
  logo,
  title,
  detail,
  tag,
  footer,
  dashed = false,
}: FixtureCardProps) {
  const classes = [styles.card, emphasis ? styles.emphasis : '', dashed ? styles.dashed : '']
    .filter(Boolean)
    .join(' ')
  return (
    <Link to={to} className={classes}>
      <span className={styles.top}>
        {live
          ? <span className={styles.live}><span className={styles.dot} aria-hidden="true" />LIVE</span>
          : <span className={emphasis ? styles.whenStrong : styles.when}>{when}</span>}
        {(time ?? tag) && <span className={styles.tag}>{tag ?? time}</span>}
      </span>
      {sides
        ? (
          <span className={styles.sides}>
            <Side side={sides[0]} />
            <Side side={sides[1]} />
          </span>
        )
        : (
          <span className={styles.event}>
            {logo && <span className={styles.eventLogo}>{logo}</span>}
            <span className={styles.eventText}>
              {title && <span className={styles.eventTitle}>{title}</span>}
              {detail && <span className={styles.eventDetail}>{detail}</span>}
            </span>
          </span>
        )}
      {footer && <span className={styles.footer}>{footer}</span>}
    </Link>
  )
}

/** The sideways row FixtureCards sit in. Scrolls on its own; the page never does. */
export function FixtureCardRail({ children }: { children: ReactNode }) {
  return <div className={styles.rail}>{children}</div>
}
